'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { twJoin } from 'tailwind-merge';
import { ImageFragment } from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import OptimizedImage from '@/ui/components/shared/OptimizedImage';
import {
  ProductBadgeOverlay,
} from '@/ui/components/product-badge/product-badge-overlay';
import type { ProductBadgeModel } from '@/models/product-badge-model';
import { buildImageAltText } from './build-image-alt-text';
import { ProductImageLightbox } from './product-image-lightbox';

export interface ProductImageGalleryProps {
  images: ImageFragment[];
  title: string;
  colorName?: string;
  // Resets the hero back to the first image whenever the selection changes.
  resetKey: string;
  /** Badges shown over the hero image. */
  badges?: ProductBadgeModel[];
}

export function ProductImageGallery({
  images,
  title,
  colorName,
  resetKey,
  badges = [],
}: Readonly<ProductImageGalleryProps>) {
  const t = useTranslations('ClassicProductDetail');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  useEffect(() => {
    setActiveIndex(0);
    setIsLightboxOpen(false);
  }, [resetKey]);

  if (images.length === 0) {
    return null;
  }

  const safeIndex = Math.min(activeIndex, images.length - 1);
  const heroImage = images[safeIndex];

  const altTextFor = (image: ImageFragment, index: number) =>
    buildImageAltText({
      title,
      colorName,
      cmsAltText: image.altText,
      viewLabel: t('imageAltFallback', { index: index + 1 }),
    });

  const heroAltText = altTextFor(heroImage, safeIndex);

  // The hero stays first in the DOM (mobile order, tab order); from lg up the
  // reversed row moves the thumbnail column to its left.
  return (
    <section
      aria-label={t('productImages')}
      className="flex flex-col gap-4 lg:flex-row-reverse lg:items-stretch"
    >
      {/* The badges sit beside the button, as a button may only hold inline content. */}
      <div data-testid="product-gallery-hero" className="relative min-w-0 flex-1">
        <button
          type="button"
          onClick={() => setIsLightboxOpen(true)}
          aria-label={t('openFullscreenImage')}
          className="block h-full w-full rounded-2xl border border-neutral-10 bg-white p-6 sm:p-16"
        >
          <OptimizedImage
            image={heroImage}
            alt={heroAltText}
            width={1200}
            height={1200}
            priority={safeIndex === 0}
            sizes="(min-width: 1024px) 50vw, 100vw"
            className="mx-auto h-auto w-full object-contain"
          />
        </button>
        <ProductBadgeOverlay badges={badges} className="inset-x-5 top-5" />
      </div>

      {images.length > 1 && (
        <ul
          className={twJoin(
            'flex snap-x gap-3 overflow-x-auto pb-2',
            'lg:w-24 lg:shrink-0 lg:snap-y lg:flex-col lg:overflow-y-auto lg:overflow-x-hidden lg:pb-0',
          )}
        >
          {images.map((image, index) => (
            <li key={image.key} className="shrink-0 snap-start">
              <button
                type="button"
                onClick={() => setActiveIndex(index)}
                aria-label={t('showImage', { index: index + 1, total: images.length })}
                aria-pressed={index === safeIndex}
                className={twJoin(
                  'block rounded-xl border bg-white p-1',
                  index === safeIndex
                    ? 'border-neutral-100'
                    : 'border-neutral-10 hover:border-neutral-55',
                )}
              >
                <OptimizedImage
                  image={image}
                  alt={altTextFor(image, index)}
                  width={96}
                  height={96}
                  sizes="96px"
                  className="h-20 w-20 object-contain"
                />
              </button>
            </li>
          ))}
        </ul>
      )}

      <ProductImageLightbox
        open={isLightboxOpen}
        image={heroImage}
        altText={heroAltText}
        onClose={() => setIsLightboxOpen(false)}
      />
    </section>
  );
}
